import React, { useState, useCallback } from 'react';
import { Info } from 'lucide-react';
import { useSurveyContext } from './SurveyContext';
import { useSurveyValidation } from '../../hooks/surveys/useSurveyValidation';
import type { Question } from '../../types/surveys/publicSurvey.types';

// Import shared question components
import {
  RadioQuestion,
  CheckboxQuestion,
  SelectQuestion,
  TextQuestion,
  TextareaQuestion,
  FileUploadQuestion,
} from '../../../components/questions';

interface SurveyQuestionProps {
  question: Question;
  primaryColor: string;
}

export const SurveyQuestionNew: React.FC<SurveyQuestionProps> = ({ question, primaryColor }) => {
  const { state, actions } = useSurveyContext();
  const { validateField } = useSurveyValidation(state);
  const [showMoreInfo, setShowMoreInfo] = useState(false);

  const response = state.surveyData[question.id] || {};
  const error = state.validationErrors[question.id];
  const isTouched = state.touchedFields.has(question.id);
  const fieldError = isTouched ? validateField(question) : null;
  const displayError = error || fieldError;

  // Get current value based on question type
  const getValue = useCallback(() => {
    if (question.answerStyle === 'checkbox' || question.answerStyle === 'checkboxes') {
      return response.answerIds || [];
    } else if (question.answerStyle === 'radio' || (question.answerStyle === 'dropdown' && !question.allowMultipleAnswers)) {
      return response.answerId || '';
    } else if (question.answerStyle === 'dropdown' && question.allowMultipleAnswers) {
      return response.answerIds || [];
    } else {
      return response.value || '';
    }
  }, [question, response]);

  const handleChange = useCallback((value: any) => {
    if (question.answerStyle === 'checkbox' || question.answerStyle === 'checkboxes') {
      actions.updateField(question.id, { answerIds: value });
    } else if (question.answerStyle === 'radio' || (question.answerStyle === 'dropdown' && !question.allowMultipleAnswers)) {
      actions.updateField(question.id, { answerId: value });
    } else if (question.answerStyle === 'dropdown' && question.allowMultipleAnswers) {
      actions.updateField(question.id, { answerIds: value });
    } else {
      actions.updateField(question.id, { value });
    }
  }, [question, actions]);

  const handleBlur = useCallback(() => {
    actions.markFieldTouched(question.id);
  }, [question.id, actions]);

  const handleCommentChange = useCallback((comment: string) => {
    actions.updateField(question.id, { ...response, comment });
  }, [question.id, response, actions]);

  const renderQuestion = () => {
    // Convert survey question to match shared component interface
    const sharedQuestion = {
      ...question,
      showAnswerOptions: true, // Surveys always show answer options if answers exist
      description: question.helpText,
    };

    switch (question.answerStyle) {
      case 'radio':
        return (
          <RadioQuestion
            question={sharedQuestion}
            value={getValue() as string}
            onChange={handleChange}
            onBlur={handleBlur}
            error={displayError}
            primaryColor={primaryColor}
            moreInfo={question.moreInfo}
            isMoreInfoExpanded={showMoreInfo}
            onToggleMoreInfo={() => setShowMoreInfo(!showMoreInfo)}
          />
        );

      case 'checkbox':
      case 'checkboxes':
        return (
          <CheckboxQuestion
            question={sharedQuestion}
            value={getValue() as string[]}
            onChange={handleChange}
            onBlur={handleBlur}
            error={displayError}
            primaryColor={primaryColor}
            moreInfo={question.moreInfo}
            isMoreInfoExpanded={showMoreInfo}
            onToggleMoreInfo={() => setShowMoreInfo(!showMoreInfo)}
          />
        );

      case 'dropdown':
        return (
          <SelectQuestion
            question={sharedQuestion}
            value={getValue()}
            onChange={handleChange}
            onBlur={handleBlur}
            error={displayError}
            primaryColor={primaryColor}
            isMulti={question.allowMultipleAnswers}
            moreInfo={question.moreInfo}
            isMoreInfoExpanded={showMoreInfo}
            onToggleMoreInfo={() => setShowMoreInfo(!showMoreInfo)}
          />
        );

      case 'textarea':
        return (
          <TextareaQuestion
            question={sharedQuestion}
            value={getValue() as string}
            onChange={handleChange}
            onBlur={handleBlur}
            error={displayError}
            primaryColor={primaryColor}
            moreInfo={question.moreInfo}
            isMoreInfoExpanded={showMoreInfo}
            onToggleMoreInfo={() => setShowMoreInfo(!showMoreInfo)}
          />
        );

      case 'text':
      case 'email':
      case 'url':
      case 'number':
      case 'date':
        return (
          <TextQuestion
            question={sharedQuestion}
            value={getValue() as string}
            onChange={handleChange}
            onBlur={handleBlur}
            error={displayError}
            primaryColor={primaryColor}
            moreInfo={question.moreInfo}
            isMoreInfoExpanded={showMoreInfo}
            onToggleMoreInfo={() => setShowMoreInfo(!showMoreInfo)}
          />
        );

      case 'file':
      case 'file_upload':
        return (
          <FileUploadQuestion
            question={sharedQuestion}
            value={response.fileData || response.file || null}
            onChange={(fileData) => actions.updateField(question.id, { fileData })}
            onBlur={handleBlur}
            error={displayError}
            primaryColor={primaryColor}
            moreInfo={question.moreInfo}
            isMoreInfoExpanded={showMoreInfo}
            onToggleMoreInfo={() => setShowMoreInfo(!showMoreInfo)}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div id={`question-${question.id}`}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {question.name}
        {question.required && <span className="text-red-500 ml-1">*</span>}
        {question.helpText && !showMoreInfo && (
          <span className="ml-2 text-xs text-gray-500">({question.helpText})</span>
        )}
        {question.moreInfo && !showMoreInfo && (
          <button
            type="button"
            onClick={() => setShowMoreInfo(!showMoreInfo)}
            className="ml-2 text-blue-600 hover:text-blue-700"
          >
            <Info size={16} className="inline" />
          </button>
        )}
      </label>

      {renderQuestion()}

      {question.showComments && (
        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700">
            Comments
          </label>
          <textarea
            value={response.comment || ''}
            onChange={(e) => handleCommentChange(e.target.value)}
            onBlur={handleBlur}
            rows={2}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
      )}

      {question.options?.useCharacterLimit && question.options?.characterLimit && response.value && (
        <div className="mt-1 text-xs text-gray-500">
          {(response.value as string).length} / {question.options.characterLimit} characters
        </div>
      )}
    </div>
  );
};